import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import {
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Upload,
  WifiOff,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  DISMISSED_UPLOADS_KEY,
  RecordingUploadJob,
  RecordingUploadStatus,
  dismissUploadJob,
  syncUploadJobs,
} from "../store/slices/recordingUploadSlice";
import {
  UploadJob,
  cancelUploadJob,
  fetchUploadJobs,
  requestRecordingUpload,
} from "../lib/devices";

const OPEN: RecordingUploadStatus[] = [
  "QUEUED_OFFLINE",
  "PENDING_EDGE_ACK",
  "QUEUED",
  "UPLOADING",
  "RETRYING",
  "STITCHING",
];
// Failures a retry cannot fix: the data is gone, or the job was closed.
const FINAL_CODES = ["EVICTED_LOCALLY", "NO_DATA", "JOB_CLOSED"];

const formatTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const toUpload = (j: UploadJob): RecordingUploadJob => ({
  jobId: j.upload_id,
  batchId: j.batch_id,
  recordingId: j.recording_id,
  deviceId: j.device_id,
  deviceName: j.device_name,
  deviceOnline: j.device_online,
  status: j.status as RecordingUploadStatus,
  progress: j.progress_percent,
  reason: j.reason,
  code: j.code,
  urgent: j.priority === "urgent",
  sliceRange: `${formatTime(j.req_start_time)} - ${formatTime(j.req_end_time)}`,
  startTime: new Date(j.created_at).getTime(),
  window: {
    start: j.req_start_time,
    end: j.req_end_time,
    pipelines: j.req_pipelines ?? [],
  },
});

const statusText = (u: RecordingUploadJob) => {
  switch (u.status) {
    case "QUEUED_OFFLINE":
      return "Waiting for the robot to connect";
    case "PENDING_EDGE_ACK":
      return "Sent to the robot";
    case "QUEUED":
      // Back in the queue part way: an urgent upload took over (F-43).
      return u.progress > 0
        ? `Paused at ${u.progress}% for an upload that goes first`
        : "Queued on the robot";
    case "STITCHING":
      return "Processing in the cloud";
    case "FAILED":
      return "Failed";
    default:
      return `Uploading ${u.progress}%`;
  }
};

/**
 * Every recording upload of the org, on every page: live from the SSE stream,
 * and from the server on load and every 30 s while one is open, so a reload or
 * a missed event never shows a stale state.
 */
const RecordingUploadsPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const uploads = useAppSelector((s) => s.recordingUploadReducer.activeUploads);
  const dismissed = useAppSelector((s) => s.recordingUploadReducer.dismissed);
  const [expanded, setExpanded] = useState(true);
  const [confirming, setConfirming] = useState<string | null>(null);

  const list = Object.values(uploads)
    .filter((u) => u.status !== "CANCELLED")
    .sort((a, b) => b.startTime - a.startTime);
  const open = list.filter((u) => OPEN.includes(u.status));

  const { data, refetch } = useQuery({
    queryKey: ["recordingUploads"],
    queryFn: async () => ({
      fetchedAt: Date.now(),
      jobs: (await fetchUploadJobs()).map(toUpload),
    }),
    refetchInterval: (q) =>
      open.length > 0 || q.state.data?.jobs.some((j) => OPEN.includes(j.status))
        ? 30_000
        : false,
  });

  useEffect(() => {
    if (data) dispatch(syncUploadJobs(data));
  }, [data, dispatch]);

  // A job seen only live has no window or device name yet.
  const unknown = list
    .filter((u) => !u.window)
    .map((u) => u.jobId)
    .join();
  useEffect(() => {
    if (unknown) refetch();
  }, [unknown, refetch]);

  useEffect(() => {
    try {
      localStorage.setItem(DISMISSED_UPLOADS_KEY, JSON.stringify(dismissed));
    } catch {
      // Dismissals then last until a reload.
    }
  }, [dismissed]);

  if (list.length === 0) return null;

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["recordingUploads"] });

  const cancel = async (u: RecordingUploadJob) => {
    setConfirming(null);
    try {
      await cancelUploadJob(u.deviceId, u.jobId);
    } catch {
      toast.error("Could not cancel the upload");
    }
    refresh();
  };

  const retry = async (u: RecordingUploadJob) => {
    if (!u.window) return;
    try {
      await requestRecordingUpload(
        u.deviceId,
        [
          {
            recording_id: u.recordingId,
            start_time: u.window.start,
            end_time: u.window.end,
            pipelines: u.window.pipelines,
          },
        ],
        { urgent: u.urgent },
      );
      dispatch(dismissUploadJob(u.jobId));
    } catch (e) {
      toast.error("Could not retry the upload", {
        description:
          isAxiosError(e) && e.response?.status === 507
            ? "Storage quota exceeded"
            : undefined,
      });
    }
    refresh();
  };

  const failedCount = list.length - open.length;

  return (
    <div className="fixed inset-x-2 bottom-2 z-50 overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900 shadow-2xl sm:inset-x-auto sm:bottom-4 sm:right-4 sm:w-96">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        className="flex w-full items-center gap-2 border-b border-neutral-800 bg-neutral-950/50 p-3 text-left hover:bg-neutral-900/50"
      >
        <Upload size={16} className="shrink-0 text-blue-400" />
        <span className="flex-1 truncate text-sm font-medium text-neutral-200">
          Recording uploads
        </span>
        {open.length > 0 && (
          <span className="rounded-full bg-blue-500/20 px-2 py-0.5 text-xs text-blue-400">
            {open.length} active
          </span>
        )}
        {failedCount > 0 && (
          <span className="rounded-full bg-red-500/20 px-2 py-0.5 text-xs text-red-400">
            {failedCount} failed
          </span>
        )}
        {expanded ? (
          <ChevronDown size={16} className="text-neutral-400" />
        ) : (
          <ChevronUp size={16} className="text-neutral-400" />
        )}
      </button>

      {expanded && (
        <div className="max-h-[50vh] divide-y divide-neutral-800 overflow-y-auto">
          {list.map((u) => {
            const failed = u.status === "FAILED";
            const offline =
              !failed &&
              u.deviceOnline === false &&
              u.status !== "QUEUED_OFFLINE";
            return (
              <div key={u.jobId} className="space-y-1.5 p-3">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 text-xs text-neutral-200">
                      <span className="truncate">
                        {u.deviceName || "Robot"}
                      </span>
                      {u.urgent && (
                        <span
                          className="shrink-0 rounded bg-amber-500/15 px-1.5 text-[10px] font-medium uppercase tracking-wide text-amber-300"
                          title="Uploads before this robot's other uploads"
                        >
                          First
                        </span>
                      )}
                    </div>
                    <div className="truncate font-mono text-[11px] text-neutral-500">
                      {u.sliceRange || u.recordingId}
                    </div>
                  </div>
                  {!failed && (
                    <button
                      type="button"
                      onClick={() =>
                        confirming === u.jobId
                          ? cancel(u)
                          : setConfirming(u.jobId)
                      }
                      onBlur={() => setConfirming(null)}
                      title="Cancels this upload and any requested with it"
                      className={`shrink-0 rounded px-2 py-1 text-xs ${
                        confirming === u.jobId
                          ? "bg-red-500/20 text-red-300"
                          : "text-neutral-400 hover:bg-neutral-800 hover:text-neutral-200"
                      }`}
                    >
                      {confirming === u.jobId ? "Confirm cancel" : "Cancel"}
                    </button>
                  )}
                  {failed &&
                    u.window &&
                    !FINAL_CODES.includes(u.code ?? "") && (
                      <button
                        type="button"
                        onClick={() => retry(u)}
                        className="flex shrink-0 items-center gap-1 rounded px-2 py-1 text-xs text-neutral-300 hover:bg-neutral-800"
                      >
                        <RotateCcw size={12} /> Retry
                      </button>
                    )}
                  {failed && (
                    <button
                      type="button"
                      onClick={() => dispatch(dismissUploadJob(u.jobId))}
                      aria-label="Dismiss"
                      className="shrink-0 rounded p-1 text-neutral-500 hover:bg-neutral-800 hover:text-neutral-200"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>

                <div
                  className={`text-xs ${failed ? "text-red-400" : u.status === "UPLOADING" ? "text-blue-400" : "text-amber-400"}`}
                >
                  {statusText(u)}
                </div>
                {failed && u.reason && (
                  <div className="break-words text-xs text-red-300/80">
                    {u.reason}
                  </div>
                )}
                {offline && (
                  <div className="flex items-center gap-1 text-xs text-amber-300">
                    <WifiOff size={12} className="shrink-0" />
                    Robot offline: the upload resumes when it reconnects
                  </div>
                )}
                {u.status === "UPLOADING" && (
                  <div className="h-1.5 overflow-hidden rounded-full bg-neutral-800">
                    <div
                      className="h-full bg-blue-500 transition-all duration-300"
                      style={{ width: `${u.progress}%` }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RecordingUploadsPanel;
